import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { Badge } from '../../lib/shadcn/badge'
import { Button } from '../../lib/shadcn/button'
import { cn } from '../../lib/shadcn/utils'
import { AlertCircle, Sparkles, Target } from 'lucide-react'
import { HelpText } from '../../components/HelpText'

type BagSize = 'Small' | 'Large'

interface Totals {
  forecast: number
  actual: number
  missed: number
  missedShare: number | null
}

interface AccuracyRow {
  product: string
  size: BagSize
  forecast: number | null
  average: number
  actual: number
}

interface AccuracyWeek {
  weekStart: string
  weekEnd: string
  inProgress: boolean
  hasForecast: boolean
  generatedAt: string | null
  model: string | null
  ai: Totals | null
  average: Totals
  rows: AccuracyRow[]
}

export interface ForecastAccuracyData {
  thisWeekStart: string
  savedWeeks: string[]
  weeks: AccuracyWeek[]
}

const LIST_LIMIT = 10

const SIZE_BADGE_CLASS: Record<BagSize, string> = {
  Small: 'bg-chart-1/15 text-chart-1 border-chart-1/30',
  Large: 'bg-chart-2/15 text-chart-2 border-chart-2/30',
}

function shortDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function pct(share: number | null): string {
  return share == null ? '—' : `${Math.round(share * 100)}%`
}

/** "+3 over", "2 under", "on target" */
function offBy(predicted: number, actual: number): string {
  const d = predicted - actual
  if (d === 0) return 'on target'
  return d > 0 ? `${d} over` : `${-d} under`
}

function offClass(predicted: number, actual: number): string {
  const d = predicted - actual
  if (d === 0) return 'text-green-600 dark:text-green-500'
  return d > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-red-600 dark:text-red-500'
}

function TotalsTile({
  title,
  icon,
  totals,
  empty,
}: {
  title: string
  icon: ReactNode
  totals: Totals | null
  empty?: ReactNode
}) {
  return (
    <div className="rounded-lg border border-border p-3 min-w-0">
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        {icon}
        {title}
      </p>
      {totals ? (
        <>
          <p className="text-lg font-semibold tabular-nums leading-tight mt-1">
            {totals.forecast.toLocaleString()} bags
            <span className="text-xs font-normal text-muted-foreground ml-1">
              vs {totals.actual.toLocaleString()} ordered
            </span>
          </p>
          <p className={cn('text-xs tabular-nums', offClass(totals.forecast, totals.actual))}>
            Total: {offBy(totals.forecast, totals.actual)}
          </p>
          <p className="text-xs text-muted-foreground tabular-nums">
            Flavor by flavor: off by {totals.missed.toLocaleString()} bags ({pct(totals.missedShare)} of ordered)
          </p>
        </>
      ) : (
        <div className="text-sm text-muted-foreground mt-1">{empty}</div>
      )}
    </div>
  )
}

// Last week's forecast vs what was actually ordered, per flavor and bag size,
// next to a simple 3-week average for comparison.
export default function ForecastAccuracyCard({
  data,
  loading,
  error,
}: {
  data: ForecastAccuracyData | undefined
  loading: boolean
  error: string | null
}) {
  const weeks = data?.weeks ?? []
  const [selected, setSelected] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  // Default: the latest finished week that has a saved forecast, else the
  // latest finished week.
  useEffect(() => {
    if (selected || weeks.length === 0) return
    const finished = weeks.filter(w => !w.inProgress)
    setSelected((finished.find(w => w.hasForecast) ?? finished[0] ?? weeks[0]!).weekStart)
  }, [weeks, selected])

  const week = weeks.find(w => w.weekStart === selected) ?? null

  // A saved forecast for this week or later, to say when it can be checked.
  const nextSaved = data?.savedWeeks.find(w => w >= data.thisWeekStart) ?? null

  const rows = useMemo(() => {
    if (!week) return []
    const miss = (r: AccuracyRow) => Math.abs((r.forecast ?? r.average) - r.actual)
    return [...week.rows].sort((a, b) => miss(b) - miss(a) || b.actual - a.actual)
  }, [week])
  const shownRows = showAll ? rows : rows.slice(0, LIST_LIMIT)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="w-4 h-4" />
          Forecast accuracy
        </CardTitle>
        <CardDescription>
          <HelpText>
            What the forecast said for a week vs the bags actually ordered that week, per flavor and size. The
            3-week average is what you&apos;d get by just averaging the weeks before; if the AI forecast isn&apos;t
            beating it, trust it less. Over means more bags were forecast than ordered.
          </HelpText>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {loading ? (
          <>
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </>
        ) : !week ? (
          <p className="py-10 text-sm text-muted-foreground text-center">No order history yet.</p>
        ) : (
          <>
            <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
              {weeks.map(w => (
                <Button
                  key={w.weekStart}
                  variant={w.weekStart === selected ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2.5 text-xs shrink-0"
                  onClick={() => {
                    setSelected(w.weekStart)
                    setShowAll(false)
                  }}
                >
                  {w.inProgress ? 'This week' : `Week of ${shortDate(w.weekStart)}`}
                  {w.hasForecast && <Sparkles className="w-3 h-3 ml-1 text-chart-1" />}
                </Button>
              ))}
            </div>

            {week.inProgress && (
              <p className="text-xs text-muted-foreground">
                This week isn&apos;t over yet, so orders are still coming in.
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TotalsTile
                title={`AI forecast, ${shortDate(week.weekStart)} – ${shortDate(week.weekEnd)}`}
                icon={<Sparkles className="w-3.5 h-3.5" />}
                totals={week.ai}
                empty={
                  <>
                    No forecast was saved for this week.
                    {nextSaved && (
                      <>
                        {' '}
                        The forecast for the week of {shortDate(nextSaved)} is saved and can be checked after{' '}
                        {shortDate(
                          new Date(new Date(`${nextSaved}T12:00:00`).getTime() + 6 * 86400000)
                            .toISOString()
                            .slice(0, 10)
                        )}
                        .
                      </>
                    )}
                    {!nextSaved && ' Regenerate the forecast before a week starts to check it afterwards.'}
                  </>
                }
              />
              <TotalsTile
                title="3-week average"
                icon={<Target className="w-3.5 h-3.5" />}
                totals={week.average}
              />
            </div>

            <div>
              <p className="text-sm font-medium mb-1">Biggest misses first</p>
              {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No bags ordered or forecast that week.</p>
              ) : (
                <ul className="divide-y">
                  {shownRows.map(r => {
                    const predicted = r.forecast ?? r.average
                    return (
                      <li key={`${r.product}-${r.size}`} className="py-1.5 text-sm">
                        <div className="flex items-baseline gap-2">
                          <span className="min-w-0 flex-1 truncate font-medium">
                            {r.product}
                            <Badge
                              variant="outline"
                              className={cn('ml-1.5 font-normal text-[11px] px-1 py-0', SIZE_BADGE_CLASS[r.size])}
                            >
                              {r.size}
                            </Badge>
                          </span>
                          <span className={cn('shrink-0 font-semibold tabular-nums', offClass(predicted, r.actual))}>
                            {offBy(predicted, r.actual)}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground tabular-nums">
                          {r.forecast != null && <>Forecast {r.forecast} · </>}
                          Ordered {r.actual} · 3-wk avg {r.average}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
              {rows.length > LIST_LIMIT && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs mt-1" onClick={() => setShowAll(v => !v)}>
                  {showAll ? 'Show fewer' : `Show all ${rows.length}`}
                </Button>
              )}
            </div>

            <div>
              <p className="text-sm font-medium mb-1">Week by week</p>
              <div className="rounded-md border border-border overflow-hidden">
                <table className="w-full text-xs tabular-nums">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-4 py-4">Week of</th>
                      <th className="text-right font-medium px-4 py-4">Ordered</th>
                      <th className="text-right font-medium px-4 py-4">AI off by</th>
                      <th className="text-right font-medium px-4 py-4">Avg off by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map(w => (
                      <tr
                        key={w.weekStart}
                        className={cn(
                          'border-t border-border cursor-pointer hover:bg-muted/40',
                          w.weekStart === selected && 'bg-muted/60'
                        )}
                        onClick={() => {
                          setSelected(w.weekStart)
                          setShowAll(false)
                        }}
                      >
                        <td className="px-4 py-4">
                          {shortDate(w.weekStart)}
                          {w.inProgress && <span className="text-muted-foreground"> (so far)</span>}
                        </td>
                        <td className="px-4 py-4 text-right">{w.average.actual.toLocaleString()}</td>
                        <td className="px-4 py-4 text-right">
                          {w.ai ? `${w.ai.missed.toLocaleString()} (${pct(w.ai.missedShare)})` : '—'}
                        </td>
                        <td className="px-4 py-4 text-right">
                          {w.average.missed.toLocaleString()} ({pct(w.average.missedShare)})
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Off by = bags missed adding up every flavor and size, and how much that is of what was ordered.
                Lower is better.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
